import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { photoUrl } from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Upload, X, Plus, ChevronRight, ChevronLeft, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import imageCompression from "browser-image-compression";
import MapPicker from "@/components/app/MapPicker";

/* ──────────────────────────────── Constants ─────────────────────────────── */
// Categories where the property has multiple units/rooms listed separately
const MULTI_UNIT_CATS = ["PG / Hostel", "Shared Flat"];

const CATEGORIES = [
  { value: "Shared Flat",             label: "Shared Flat" },
  { value: "Independent Flat / Villa", label: "Independent Flat / Villa" },
  { value: "1RK / Studio",            label: "1RK / Studio" },
  { value: "PG / Hostel",             label: "PG / Hostel" },
];

// Common property-level amenities (apply to the whole building/flat)
const AMEN_COMMON = [
  "WiFi", "Power Backup", "Parking", "Lift", "Security", "Gym",
  "CCTV", "Garden", "Club House", "Swimming Pool", "Laundry",
];

// Room/unit-level furnishing items
const AMEN_UNIT = [
  "AC", "Beds", "Geyser", "Fans", "Washing Machine", "Fridge",
  "Chimney", "TV", "Wardrobe", "Sofa", "Stove", "Water Purifier",
  "Modular Kitchen", "Dining Table",
];

const FURNISH_ITEMS = AMEN_UNIT; // same list used for single-mode furnishing

const RULES = [
  "No smoking", "No pets", "No loud music after 10pm",
  "No overnight guests", "Vegetarian only", "No alcohol",
];

const TENANT_PREFS = ["Family", "Male", "Female", "Company", "Students", "Professionals"];

/* ──────────────────────────────── Helpers ───────────────────────────────── */
const normalizePhotos = (photos) =>
  (photos || []).filter((p) => typeof p === "string" && p.trim());

const emptyUnit = (idx = 0) => ({
  title: "",
  room_type: "private",
  occupancy: 1,
  bathroom_type: "attached",
  ac: false,
  balcony: false,
  carpet_area: "",
  floor: "",
  available_from: new Date().toISOString().slice(0, 10),
  rent: 0,
  deposit: 0,
  electricity_included: false,
  units_available: 1,
  amenities: [],
  photos: [],
  display_order: idx,
});

const emptyListingForm = {
  listing_category: "Shared Flat",
  property_type: "apartment",
  country: "India",
  city: "",
  locality: "",
  address: "",
  latitude: null,
  longitude: null,
  // property-level fields
  bhk: "1BHK",
  total_bedrooms: 1,
  bathrooms: 1,
  balcony: false,
  carpet_area: "",
  furnishing: "semi",
  furnished_items: [],
  floor: "",
  age: "",
  available_from: new Date().toISOString().slice(0, 10),
  lease_duration_months: 11,
  rent: 12000,
  deposit: 24000,
  electricity_included: false,
  units_available: 1,
  tenant_pref: [],
  amenities: [],
  house_rules: [],
  photos: [],
  units: [emptyUnit(0)],
  preferences: {
    gender: "any", occupation: "any", food_pref: "any",
    smoking_allowed: false, drinking_allowed: false, pets_allowed: false,
  },
  title: "",
  description: "",
};

const formFromListing = (l) => ({
  ...emptyListingForm,
  listing_category: l.listing_category || "Shared Flat",
  property_type:    l.property_type    || "apartment",
  country: "India",
  city:     l.city     || "",
  locality: l.locality || "",
  address:  l.address  || "",
  latitude:  l.latitude  || null,
  longitude: l.longitude || null,
  bhk:            l.bhk            || "1BHK",
  total_bedrooms: l.total_bedrooms || 1,
  furnishing:     l.furnishing     || "semi",
  furnished_items: l.amenities     || [],
  available_from:  l.available_from
    ? l.available_from.slice(0, 10)
    : new Date().toISOString().slice(0, 10),
  lease_duration_months: l.lease_duration_months || 11,
  rent:    l.rent    || 0,
  deposit: l.deposit || 0,
  amenities:   l.amenities    || [],
  house_rules: l.house_rules  || [],
  photos: normalizePhotos(l.photos),
  units:  l.units && l.units.length > 0 ? l.units : [emptyUnit(0)],
  preferences: { ...emptyListingForm.preferences, ...(l.preferences || {}) },
  title:       l.title       || "",
  description: l.description || "",
});

/* ──────────────────────────────── Small UI ──────────────────────────────── */
const Section = ({ title, subtitle, children }) => (
  <section className="p-6 bg-white border border-stone-200 rounded-2xl shadow-sm">
    <div className="mb-5">
      <h2 className="font-display text-lg font-semibold text-stone-800">{title}</h2>
      {subtitle && <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>}
    </div>
    <div className="grid grid-cols-2 gap-4">{children}</div>
  </section>
);

const Field = ({ label, children, span, hint }) => (
  <div className={span ? "col-span-2" : ""}>
    <Label className="text-xs font-medium text-stone-500 uppercase tracking-wide">{label}</Label>
    {hint && <p className="text-[10px] text-stone-400 mt-0.5">{hint}</p>}
    <div className="mt-1">{children}</div>
  </div>
);

const Chip = ({ label, active, onClick, color = "green" }) => {
  const on = color === "red"
    ? "bg-red-50 text-red-700 border-red-300"
    : "bg-[#2A4B3C]/10 text-[#2A4B3C] border-[#2A4B3C]/40";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer select-none ${
        active ? on : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
      }`}
    >
      {label}
    </button>
  );
};

const FurnishPicker = ({ form, upd }) => (
  <Field label="Furnishing status" span>
    <div className="flex gap-3 mb-3">
      {[
        { v: "unfurnished", l: "Unfurnished" },
        { v: "semi",        l: "Semi-furnished" },
        { v: "full",        l: "Fully furnished" },
      ].map(({ v, l }) => (
        <button
          key={v}
          type="button"
          onClick={() => upd("furnishing", v)}
          className={`flex-1 text-sm rounded-lg border py-2 transition-all ${
            form.furnishing === v
              ? "bg-[#2A4B3C] text-white border-[#2A4B3C]"
              : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
    {form.furnishing !== "unfurnished" && (
      <div className="flex flex-wrap gap-1.5">
        {FURNISH_ITEMS.map((a) => (
          <Chip
            key={a} label={a}
            active={(form.furnished_items || []).includes(a)}
            onClick={() => {
              const cur = form.furnished_items || [];
              upd("furnished_items", cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]);
            }}
          />
        ))}
      </div>
    )}
  </Field>
);

/* ──────────────────────────────── Unit Card ─────────────────────────────── */
function UnitCard({ unit, idx, isMultiUnitProp, onChange, onRemove, uploading, onUpload, canRemove }) {
  const upd = (k, v) => onChange(idx, k, v);
  const toggleAmen = (a) => {
    const cur = unit.amenities || [];
    upd("amenities", cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]);
  };

  return (
    <div className="border border-stone-200 rounded-2xl bg-stone-50 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 bg-white border-b border-stone-100">
        <span className="text-sm font-semibold text-stone-700">
          {isMultiUnitProp ? `Room category ${idx + 1}` : "Room details"}
        </span>
        {canRemove && (
          <button type="button" onClick={() => onRemove(idx)} className="text-xs text-red-500 hover:text-red-700">
            Remove
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Label — for multi-unit properties this matters; hide for single-property */}
        {isMultiUnitProp && (
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Room category label</Label>
            <Input
              className="mt-1"
              placeholder='e.g. "Double Sharing AC Room"'
              value={unit.title}
              onChange={(e) => upd("title", e.target.value)}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Room type</Label>
            <Select value={unit.room_type} onValueChange={(v) => upd("room_type", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["private", "shared", "entire"].map((v) => (
                  <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Occupancy only relevant for PG/Hostel/Shared */}
          {isMultiUnitProp && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-stone-500">Occupancy (max persons)</Label>
              <Input type="number" min={1} className="mt-1" value={unit.occupancy} onChange={(e) => upd("occupancy", Number(e.target.value))} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Bathroom</Label>
            <Select value={unit.bathroom_type} onValueChange={(v) => upd("bathroom_type", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="attached">Attached</SelectItem>
                <SelectItem value="common">Common / Shared</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Balcony</Label>
            <Select value={unit.balcony ? "yes" : "no"} onValueChange={(v) => upd("balcony", v === "yes")}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Carpet area (sq ft)</Label>
            <Input type="number" className="mt-1" placeholder="e.g. 250" value={unit.carpet_area} onChange={(e) => upd("carpet_area", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Floor</Label>
            <Input className="mt-1" placeholder="e.g. 2nd of 4" value={unit.floor} onChange={(e) => upd("floor", e.target.value)} />
          </div>
        </div>

        {/* Rent, deposit, electricity, units available — always per unit */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Rent (₹/mo)</Label>
            <Input type="number" className="mt-1" value={unit.rent} onChange={(e) => upd("rent", Number(e.target.value))} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Deposit (₹)</Label>
            <Input type="number" className="mt-1" value={unit.deposit} onChange={(e) => upd("deposit", Number(e.target.value))} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Electricity</Label>
            <Select value={unit.electricity_included ? "included" : "extra"} onValueChange={(v) => upd("electricity_included", v === "included")}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="included">Included in rent</SelectItem>
                <SelectItem value="extra">Charged separately</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Units available</Label>
            <Input type="number" min={1} className="mt-1" value={unit.units_available} onChange={(e) => upd("units_available", Number(e.target.value))} />
          </div>
        </div>

        {/* Available from — per unit (different rooms may be available at different times) */}
        {isMultiUnitProp && (
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Available from</Label>
            <Input type="date" className="mt-1" value={unit.available_from || ""} onChange={(e) => upd("available_from", e.target.value)} />
          </div>
        )}

        {/* Room-specific amenities */}
        <div className="pt-2 border-t border-stone-200">
          <Label className="text-xs uppercase tracking-wide text-stone-500 mb-2 block">
            Items in this room
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {AMEN_UNIT.map((a) => (
              <Chip key={a} label={a} active={(unit.amenities || []).includes(a)} onClick={() => toggleAmen(a)} />
            ))}
          </div>
        </div>

        {/* Per-unit photos */}
        <div className="pt-2 border-t border-stone-200">
          <Label className="text-xs uppercase tracking-wide text-stone-500 mb-2 block">
            {isMultiUnitProp ? "Photos for this room/category" : "Room photos"}
          </Label>
          <label className="flex items-center gap-2 cursor-pointer border border-dashed border-stone-300 rounded-xl p-3 text-stone-500 text-xs hover:border-[#2A4B3C]/50 transition-colors">
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading…" : "Upload photos"}
            <input type="file" multiple accept="image/*" hidden onChange={(e) => onUpload(e, idx)} />
          </label>
          {(unit.photos || []).length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {unit.photos.map((p, i) => (
                <div key={i} className="relative w-14 h-14">
                  <img src={photoUrl(p)} alt="" className="w-14 h-14 object-cover rounded-lg border border-stone-200" />
                  <button
                    type="button"
                    onClick={() => upd("photos", unit.photos.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-stone-300 grid place-items-center shadow"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────── Step bar ──────────────────────────────── */
function StepBar({ step }) {
  const steps = ["Category & Location", "Property Details", "Photos & Publish"];
  return (
    <div className="flex items-center mb-8">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              i < step  ? "bg-[#2A4B3C] text-white"
              : i === step ? "bg-[#2A4B3C] text-white ring-4 ring-[#2A4B3C]/20"
              : "bg-stone-200 text-stone-400"
            }`}>
              {i < step ? "✓" : i + 1}
            </div>
            <span className={`text-xs hidden sm:block ${i === step ? "text-stone-800 font-semibold" : "text-stone-400"}`}>
              {s}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-px mx-3 ${i < step ? "bg-[#2A4B3C]" : "bg-stone-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ──────────────────────────────── Main ──────────────────────────────────── */
export default function PostListing() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!editId);
  const [form, setForm] = useState(emptyListingForm);

  // Whether the selected category is multi-unit by nature
  const isMultiUnitProp = MULTI_UNIT_CATS.includes(form.listing_category);

  /* ── load for edit ── */
  useEffect(() => {
    if (!editId) return;
    setLoadingEdit(true);
    api.get(`/listings/${editId}`)
      .then((r) => {
        const loaded = formFromListing(r.data.listing);
        setForm(loaded);
      })
      .catch(() => toast.error("Could not load listing"))
      .finally(() => setLoadingEdit(false));
  }, [editId]);

  if (!user) { nav("/login"); return null; }

  /* ── form helpers ── */
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updPref = (k, v) => setForm((f) => ({ ...f, preferences: { ...f.preferences, [k]: v } }));
  const toggleArr = (key, val) => {
    const arr = form[key] || [];
    upd(key, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  /* ── unit helpers ── */
  const addUnit = () => {
    if (form.units.length >= 5) { toast.error("Maximum 5 categories allowed"); return; }
    setForm((f) => ({ ...f, units: [...f.units, emptyUnit(f.units.length)] }));
  };
  const removeUnit = (idx) => {
    if (form.units.length <= 1) return; // always keep at least one
    setForm((f) => ({ ...f, units: f.units.filter((_, i) => i !== idx) }));
  };
  const updUnit = (idx, key, value) =>
    setForm((f) => ({ ...f, units: f.units.map((u, i) => (i === idx ? { ...u, [key]: value } : u)) }));

  /* ── photo upload (listing-level) ── */
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); continue; }
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10MB`); continue; }
        const c = await imageCompression(f, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
        const fd = new FormData();
        fd.append("file", c);
        const r = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(r.data.path);
      }
      upd("photos", [...form.photos, ...paths]);
      toast.success(`${paths.length} photo(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  /* ── photo upload (unit-level) ── */
  const handleUnitUpload = async (e, idx) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        if (!f.type.startsWith("image")) { toast.error(`${f.name} is not an image`); continue; }
        const c = await imageCompression(f, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
        const fd = new FormData();
        fd.append("file", c);
        const r = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(r.data.path);
      }
      updUnit(idx, "photos", [...(form.units[idx]?.photos || []), ...paths]);
      toast.success(`${paths.length} photo(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  /* ── location ── */
  const handleLocationChange = (loc) =>
    setForm((f) => ({ ...f, latitude: loc.lat, longitude: loc.lng, address: loc.address || f.address }));

  const pickLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success("Location picked!");
      },
      () => toast.error("Could not get location")
    );
  };

  /* ── submit ── */
  const submit = async () => {
    if (!form.title) { toast.error("Please add a title"); return; }
    if (!form.city && !form.locality) { toast.error("Please add a city or locality"); return; }

    const effectiveRent = isMultiUnitProp && form.units.length > 0
      ? Math.min(...form.units.map((u) => u.rent || 0))
      : form.rent;

    const payload = {
      ...form,
      rent: effectiveRent,
      available_from: new Date(form.available_from).toISOString(),
      // For multi-unit properties, pass all units; for single, pass empty
      units: isMultiUnitProp ? form.units : [],
    };
    setSaving(true);
    try {
      let listingId;
      if (editId) {
        const r = await api.put(`/listings/${editId}`, payload);
        listingId = r.data.listing.id;
        toast.success("Listing updated!");
      } else {
        const r = await api.post("/listings", payload);
        listingId = r.data.listing.id;
        toast.success("Listing posted!");
      }
      nav(`/listing/${listingId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  /* ════════════════════════════════════════════════════════════
     STEP 0 — Category & Location
  ════════════════════════════════════════════════════════════ */
  const renderStep0 = () => (
    <>
      <Section title="What kind of property is this?">
        <Field label="Category" span>
          <div className="grid grid-cols-2 gap-3">
            {CATEGORIES.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => upd("listing_category", value)}
                className={`text-sm rounded-xl border px-4 py-3.5 text-left transition-all font-medium leading-tight ${
                  form.listing_category === value
                    ? "bg-[#2A4B3C] text-white border-[#2A4B3C] shadow-sm"
                    : "bg-white border-stone-200 text-stone-700 hover:border-[#2A4B3C]/40"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>
      </Section>

      <Section title="Where is it located?">
        <Field label="Country">
          <Input value={form.country} onChange={(e) => upd("country", e.target.value)} placeholder="India" />
        </Field>
        <Field label="City">
          <Input data-testid="post-city" value={form.city} onChange={(e) => upd("city", e.target.value)} placeholder="Mumbai, Indore, Bangalore…" />
        </Field>
        <Field label="Locality / Area / Pincode" span>
          <div className="relative">
            <Input
              data-testid="post-locality"
              value={form.locality}
              onChange={(e) => upd("locality", e.target.value)}
              placeholder="Koramangala, 400001, MG Road…"
              className="pr-10"
            />
            <button
              type="button" onClick={pickLocation} title="Pick my location"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-[#2A4B3C] transition-colors"
            >
              <MapPin className="w-4 h-4" />
            </button>
          </div>
        </Field>
        <Field label="Full address (optional)" span>
          <Input data-testid="post-address" value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder="Building name, street…" />
        </Field>
        <Field label="Pin on map" span hint="Drag the marker to set the exact location">
          <div className="mt-1">
            <MapPicker value={{ lat: form.latitude, lng: form.longitude }} onChange={handleLocationChange} />
          </div>
        </Field>
      </Section>
    </>
  );

  /* ════════════════════════════════════════════════════════════
     STEP 1 — Property Details (category-aware)
  ════════════════════════════════════════════════════════════ */
  const renderStep1 = () => {
    if (isMultiUnitProp) {
      return renderStep1MultiUnit();
    }
    return renderStep1Single();
  };

  /* ── STEP 1: PG / Hostel / Shared Flat ── */
  const renderStep1MultiUnit = () => (
    <>
      {/* Minimal property overview */}
      <Section
        title="Property overview"
        subtitle="Common details that apply to the entire property — rent and room specifics go in each room card below."
      >
        <Field label="Total rooms in property">
          <Input type="number" min={1} value={form.total_bedrooms} onChange={(e) => upd("total_bedrooms", Number(e.target.value))} />
        </Field>
        <Field label="Total bathrooms in property">
          <Input type="number" min={1} value={form.bathrooms} onChange={(e) => upd("bathrooms", Number(e.target.value))} />
        </Field>
        <Field label="Floor details">
          <Input placeholder="e.g. Ground + 3 floors" value={form.floor} onChange={(e) => upd("floor", e.target.value)} />
        </Field>
        <Field label="Age of property">
          <Input placeholder="e.g. 5 years" value={form.age} onChange={(e) => upd("age", e.target.value)} />
        </Field>
        <Field label="Common facilities for all tenants" span>
          <div className="flex flex-wrap gap-1.5">
            {AMEN_COMMON.map((a) => (
              <Chip key={a} label={a} active={(form.amenities || []).includes(a)} onClick={() => toggleArr("amenities", a)} />
            ))}
          </div>
        </Field>
        <Field label="House rules" span>
          <div className="flex flex-wrap gap-1.5">
            {RULES.map((r) => (
              <Chip key={r} label={r} active={(form.house_rules || []).includes(r)} onClick={() => toggleArr("house_rules", r)} color="red" />
            ))}
          </div>
        </Field>
        <Field label="Willing to rent to" span>
          <div className="flex flex-wrap gap-1.5">
            {TENANT_PREFS.map((t) => (
              <Chip key={t} label={t} active={(form.tenant_pref || []).includes(t)} onClick={() => toggleArr("tenant_pref", t)} />
            ))}
          </div>
        </Field>
      </Section>

      {/* Room / unit cards */}
      <div className="space-y-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-stone-800">Room categories</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            Add each type of room available — rent, deposit, amenities and photos per room category.
          </p>
        </div>

        {form.units.map((unit, idx) => (
          <UnitCard
            key={idx}
            unit={unit}
            idx={idx}
            isMultiUnitProp={true}
            onChange={updUnit}
            onRemove={removeUnit}
            uploading={uploading}
            onUpload={handleUnitUpload}
            canRemove={form.units.length > 1}
          />
        ))}

        {form.units.length < 5 && (
          <button
            type="button" onClick={addUnit}
            className="w-full border-2 border-dashed border-stone-300 rounded-2xl py-4 text-stone-500 text-sm font-medium hover:border-[#2A4B3C]/50 hover:text-[#2A4B3C] transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add another room category
          </button>
        )}
      </div>
    </>
  );

  /* ── STEP 1: Independent Flat / Villa / 1RK / Studio ── */
  const renderStep1Single = () => {
    const isStudio = form.listing_category === "1RK / Studio";
    return (
      <>
        <Section title="Property details">
          {!isStudio && (
            <>
              <Field label="BHK configuration">
                <Select value={form.bhk} onValueChange={(v) => upd("bhk", v)}>
                  <SelectTrigger data-testid="post-bhk"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Studio", "1RK", "1BHK", "2BHK", "3BHK", "4BHK", "4+BHK"].map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Total bedrooms">
                <Input type="number" min={0} value={form.total_bedrooms} onChange={(e) => upd("total_bedrooms", Number(e.target.value))} />
              </Field>
            </>
          )}
          <Field label="Bathrooms">
            <Input type="number" min={1} value={form.bathrooms} onChange={(e) => upd("bathrooms", Number(e.target.value))} />
          </Field>
          <Field label="Balcony">
            <Select value={form.balcony ? "yes" : "no"} onValueChange={(v) => upd("balcony", v === "yes")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Carpet area (sq ft)">
            <Input type="number" placeholder="e.g. 500" value={form.carpet_area} onChange={(e) => upd("carpet_area", e.target.value)} />
          </Field>
          <Field label="Floor details">
            <Input placeholder="e.g. 3rd of 7" value={form.floor} onChange={(e) => upd("floor", e.target.value)} />
          </Field>
          <Field label="Age of property">
            <Input placeholder="e.g. 5 years" value={form.age} onChange={(e) => upd("age", e.target.value)} />
          </Field>
          <Field label="Available from">
            <Input data-testid="post-available-from" type="date" value={form.available_from} onChange={(e) => upd("available_from", e.target.value)} />
          </Field>

          <FurnishPicker form={form} upd={upd} />

          <Field label="Rent (₹/mo)">
            <Input data-testid="post-rent" type="number" value={form.rent} onChange={(e) => upd("rent", Number(e.target.value))} />
          </Field>
          <Field label="Deposit (₹)">
            <Input data-testid="post-deposit" type="number" value={form.deposit} onChange={(e) => upd("deposit", Number(e.target.value))} />
          </Field>
          <Field label="Electricity charges">
            <Select value={form.electricity_included ? "included" : "extra"} onValueChange={(v) => upd("electricity_included", v === "included")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="included">Included in rent</SelectItem>
                <SelectItem value="extra">Charged separately</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Units available">
            <Input type="number" min={1} value={form.units_available} onChange={(e) => upd("units_available", Number(e.target.value))} />
          </Field>

          <Field label="Amenities" span>
            <div className="flex flex-wrap gap-1.5">
              {AMEN_COMMON.map((a) => (
                <Chip key={a} label={a} active={(form.amenities || []).includes(a)} onClick={() => toggleArr("amenities", a)} />
              ))}
            </div>
          </Field>

          <Field label="Willing to rent to" span>
            <div className="flex flex-wrap gap-1.5">
              {TENANT_PREFS.map((t) => (
                <Chip key={t} label={t} active={(form.tenant_pref || []).includes(t)} onClick={() => toggleArr("tenant_pref", t)} />
              ))}
            </div>
          </Field>
        </Section>

        {/* Single room detail card — no toggle, no multi-unit */}
        <div>
          <h2 className="font-display text-lg font-semibold text-stone-800 mb-3">Room details</h2>
          <UnitCard
            unit={form.units[0] || emptyUnit(0)}
            idx={0}
            isMultiUnitProp={false}
            onChange={updUnit}
            onRemove={removeUnit}
            uploading={uploading}
            onUpload={handleUnitUpload}
            canRemove={false}
          />
        </div>

        {/* House rules */}
        <Section title="House rules">
          <div className="col-span-2 flex flex-wrap gap-2">
            {RULES.map((r) => (
              <Chip key={r} label={r} active={(form.house_rules || []).includes(r)} onClick={() => toggleArr("house_rules", r)} color="red" />
            ))}
          </div>
        </Section>
      </>
    );
  };

  /* ════════════════════════════════════════════════════════════
     STEP 2 — Photos, Preferences, Title & Description
  ════════════════════════════════════════════════════════════ */
  const renderStep2 = () => (
    <>
      {/* Photos — listing-level only for single properties */}
      {!isMultiUnitProp ? (
        <Section title="Listing photos">
          <div className="col-span-2">
            <label className="flex items-center gap-2 cursor-pointer border border-dashed border-stone-300 rounded-2xl p-6 text-stone-600 hover:border-[#2A4B3C]/40 transition-colors">
              <Upload className="w-5 h-5" />
              <span>{uploading ? "Uploading…" : "Click to upload one or more photos"}</span>
              <input data-testid="post-photos" type="file" accept="image/*" multiple hidden onChange={handleUpload} />
            </label>
            {form.photos.length > 0 && (
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2">
                {form.photos.map((p) => (
                  <div key={p} className="relative">
                    <img src={photoUrl(p)} alt="" className="w-full aspect-square object-cover rounded-lg border border-stone-200" />
                    <button
                      type="button"
                      onClick={() => upd("photos", form.photos.filter((x) => x !== p))}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-stone-300 grid place-items-center shadow"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>
      ) : (
        <div className="p-4 bg-stone-100 border border-stone-200 rounded-xl text-sm text-stone-500">
          📸 Photos are added per room category in the previous step — each room card has its own uploader.
        </div>
      )}

      {/* Tenant preferences */}
      <Section title="Tenant preferences">
        <Field label="Gender preference">
          <Select value={form.preferences.gender} onValueChange={(v) => updPref("gender", v)}>
            <SelectTrigger data-testid="post-gender"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["any", "male", "female"].map((v) => (
                <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Occupation">
          <Select value={form.preferences.occupation} onValueChange={(v) => updPref("occupation", v)}>
            <SelectTrigger data-testid="post-occupation"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["any", "working", "students"].map((v) => (
                <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Food preference">
          <Select value={form.preferences.food_pref} onValueChange={(v) => updPref("food_pref", v)}>
            <SelectTrigger data-testid="post-food"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["any", "veg", "eggetarian"].map((v) => (
                <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="col-span-2 flex flex-wrap gap-4 pt-2">
          {[
            { key: "smoking_allowed", label: "Smoking allowed", testId: "post-smoking" },
            { key: "pets_allowed",    label: "Pets allowed",    testId: "post-pets" },
            { key: "drinking_allowed",label: "Drinking allowed",testId: null },
          ].map(({ key, label, testId }) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                {...(testId ? { "data-testid": testId } : {})}
                checked={form.preferences[key]}
                onCheckedChange={(v) => updPref(key, !!v)}
              />
              {label}
            </label>
          ))}
        </div>
      </Section>

      {/* Title & Description */}
      <Section title="Give your listing a title & description">
        <Field label="Title" span>
          <Input
            data-testid="post-title"
            value={form.title}
            onChange={(e) => upd("title", e.target.value)}
            placeholder={
              isMultiUnitProp
                ? 'e.g. "Spacious PG for working professionals in Koramangala"'
                : 'e.g. "Bright 2BHK near metro, semi-furnished"'
            }
          />
        </Field>
        <Field label="Description" span hint="Describe the vibe, nearby landmarks, what makes it special…">
          <Textarea
            data-testid="post-description"
            rows={5}
            value={form.description}
            onChange={(e) => upd("description", e.target.value)}
            placeholder="Well-maintained, 5 min from metro station, 24hr water, quiet locality…"
          />
        </Field>
      </Section>
    </>
  );

  /* ─── render ── */
  const steps = [renderStep0, renderStep1, renderStep2];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-1">
            {editId ? "Edit listing" : "New listing"}
          </div>
          <h1 className="font-display text-3xl font-semibold text-stone-900">
            {editId ? "Update your listing" : "Post your place"}
          </h1>
          {!editId && <p className="text-stone-500 mt-1 text-sm">Zero brokerage. Reach verified renters directly.</p>}
          {loadingEdit && <p className="text-stone-400 mt-2 animate-pulse text-sm">Loading listing details…</p>}
        </div>

        <StepBar step={step} />

        <div className="space-y-6">{steps[step]()}</div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 mt-6 border-t border-stone-200">
          {step > 0 ? (
            <Button variant="outline" className="rounded-full" onClick={() => { setStep((s) => s - 1); window.scrollTo(0, 0); }}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : <div />}
          {step < 2 ? (
            <Button className="brand-accent rounded-full px-6" onClick={() => { setStep((s) => s + 1); window.scrollTo(0, 0); }}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="post-submit"
              className="brand-accent rounded-full px-8 h-11"
              onClick={submit}
              disabled={saving}
            >
              {saving
                ? (editId ? "Saving…" : "Publishing…")
                : (editId ? "Save changes" : "Publish listing")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
